import { useState } from 'react';
import { useMatch } from 'react-router-dom';
import { Navigation } from '~/shared/routing/nav/Navigation';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { getWorkflowStageNeedsCompletedCountOptions, getWorkflowSystemNamesOptions, WORKFLOW_SYSTEM_NAME_CONSTANTS } from '@generated';
import { usePrevious } from '~/shared/hooks/usePrevious';
import { useChart } from '../../features/admissions/charts/ChartContext';

export function ChartPageNavigation() {
	const { stayId } = useChart();
	const { data: worklflowSystemNames } = useSuspenseQuery(getWorkflowSystemNamesOptions(stayId));
	const { data: needsCompleteTaskCount } = useQuery(getWorkflowStageNeedsCompletedCountOptions(stayId));

	const sectionMatch = useMatch('/charts/:patientId/:stayId/:certificationPeriodId/:section/*');
	const routeSection = sectionMatch?.params.section;

	const [expandedSections, setExpandedSections] = useState<string[]>(routeSection ? [routeSection] : []);

	//expand the section for the current route when navigation changes it (e.g. a link from another page)
	const prevRouteSection = usePrevious(routeSection);
	if (routeSection && routeSection !== prevRouteSection && !expandedSections.includes(routeSection)) {
		setExpandedSections(prev => [...prev, routeSection]);
	}

	const toggleSection = (sectionToToggle: string) => {
		setExpandedSections(prev =>
			prev.includes(sectionToToggle) ? prev.filter(section => section !== sectionToToggle) : [...prev, sectionToToggle]
		);
	};

	const includeNoa = worklflowSystemNames
		? worklflowSystemNames.filter(x => x.workflowSystemName === WORKFLOW_SYSTEM_NAME_CONSTANTS.NoticeOfAdmission).length > 0
		: false;
	const includeF2f = worklflowSystemNames
		? worklflowSystemNames.filter(x => x.workflowSystemName === WORKFLOW_SYSTEM_NAME_CONSTANTS.FacetoFace).length > 0
		: false;
	const includeOasis = worklflowSystemNames
		? worklflowSystemNames.filter(x => x.workflowSystemName === WORKFLOW_SYSTEM_NAME_CONSTANTS.Oasis302Soc).length > 0
		: false;

	return (
		<Navigation toggleSection={toggleSection} expandedSections={expandedSections} selectedRouteSection={routeSection}>
			<Navigation.NavigationSectionLink label="Overview" path="" end />
			<Navigation.NavigationSectionLink label={needsCompleteTaskCount ? `Tasks (${needsCompleteTaskCount.count})` : 'Tasks'} path="tasks" />
			<Navigation.NavigationSection label="Profile" sectionName="profile">
				<Navigation.NavigationSectionLink label="Demographics" path="demographics" />
				<Navigation.NavigationSectionLink label="Addresses" path="addresses" />
				<Navigation.NavigationSectionLink label="Advanced Directives" path="advanced-directives" />
				<Navigation.NavigationSectionLink label="Contacts" path="contacts" />
				<Navigation.NavigationSectionLink label="Immunizations" path="immunizations" />
			</Navigation.NavigationSection>
			<Navigation.NavigationSection label="Intake" sectionName="intake">
				<Navigation.NavigationSectionLink label="Referral" path="referral" />
				<Navigation.NavigationSectionLink label="Previous Admissions" path="previous-admissions" />
			</Navigation.NavigationSection>
			<Navigation.NavigationSection label="Medications" sectionName="medications">
				<Navigation.NavigationSectionLink label="Allergies" path="allergies" />
				<Navigation.NavigationSectionLink label="Medications" path="medications" />
				<Navigation.NavigationSectionLink label="Pharmacies" path="pharmacies" />
			</Navigation.NavigationSection>
			<Navigation.NavigationSection label="Orders" sectionName="orders">
				<Navigation.NavigationSectionLink label="Orders" path="list" />
				<Navigation.NavigationSectionLink label="Physicians" path="physicians" />
			</Navigation.NavigationSection>
			<Navigation.NavigationSection label="Incidents" sectionName="incidents">
				<Navigation.NavigationSectionLink label="Incidents" path="incidents" />
				<Navigation.NavigationSectionLink label="Falls" path="falls" />
			</Navigation.NavigationSection>
			<Navigation.NavigationSection label="Hospitalizations" sectionName="hospitalizations">
				<Navigation.NavigationSectionLink label="Hosplizations" path="hospitalizations" />
				<Navigation.NavigationSectionLink label="Falls" path="falls" />
			</Navigation.NavigationSection>
			<Navigation.NavigationSectionLink label="Plan of Care" path="plan-of-care" end />
			{/* <Navigation.NavigationSection label="Plan of Care" sectionName="plan-of-care">
				<Navigation.NavigationSectionLink label="Diagnoses" path="diagnoses" />
				<Navigation.NavigationSectionLink label="Goals & Interventions" path="goals-and-interventions" />
				<Navigation.NavigationSectionLink label="Discharge Plan" path="discharge-plan" />
			</Navigation.NavigationSection> */}
			<Navigation.NavigationSection label="Scheduling" sectionName="scheduling">
				<Navigation.NavigationSectionLink label="Schedule" path="schedule" />
			</Navigation.NavigationSection>
			<Navigation.NavigationSection label="Coordination" sectionName="coordination">
				<Navigation.NavigationSectionLink label="Case Conferences" path="case-conferences" />
			</Navigation.NavigationSection>
			{includeOasis ? (
				<Navigation.NavigationSection label="OASIS" sectionName="oasis">
					<Navigation.NavigationSectionLink label="OASIS Assessments" path="oasis-assessments" />
				</Navigation.NavigationSection>
			) : null}
			<Navigation.NavigationSection label="QA" sectionName="qa">
				<Navigation.NavigationSectionLink label="PoC Reviews" path="poc-reviews" />
			</Navigation.NavigationSection>
			<Navigation.NavigationSection label="Billing" sectionName="billing">
				<Navigation.NavigationSectionLink label="Insurance" path="insurance" />
				{includeNoa ? <Navigation.NavigationSectionLink label="NOA" path="noa" /> : null}
				{includeF2f ? <Navigation.NavigationSectionLink label="F2f" path="f2f" /> : null}
				<Navigation.NavigationSectionLink label="Claims" path="claims" />
			</Navigation.NavigationSection>
		</Navigation>
	);
}

// function NavigationWorkflowSectionLink({}) {}
